import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Card, Divider, Tabs, Tab, CircularProgress,
    Button, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import { loadFromIndexedDB } from './indexedDBUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const ReportDashboard = () => {
    const [localOrders, setLocalOrders] = useState([]);
    const [indexedDBOrders, setIndexedDBOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);

    const [filterType, setFilterType] = useState("month");
    const [filterValue, setFilterValue] = useState(dayjs().format('YYYY-MM'));

    useEffect(() => {
        const loadOrders = async () => {
            try {
                const storedLocal = JSON.parse(localStorage.getItem('billHistory')) || [];
                setLocalOrders(storedLocal);

                const indexedOrders = await loadFromIndexedDB();
                setIndexedDBOrders(indexedOrders);
            } catch (error) {
                console.error('Error loading orders:', error);
            } finally {
                setLoading(false);
            }
        };
        loadOrders();
    }, []);

    const getFilteredOrders = () => {
        const orders = tab === 0 ? localOrders : indexedDBOrders;

        return orders.filter(order => {
            const date = dayjs(order.timestamp);
            if (filterType === 'year') {
                return date.format('YYYY') === filterValue;
            } else if (filterType === 'month') {
                return date.format('YYYY-MM') === filterValue;
            } else if (filterType === 'week') {
                return date.year().toString() === filterValue.split('-W')[0] &&
                    `W${date.isoWeek().toString().padStart(2, '0')}` === filterValue.split('-')[1];
            }
            return true;
        });
    };

    const filteredOrders = getFilteredOrders();

    const handleDownloadPDF = () => {
        if (filteredOrders.length === 0) {
            alert("No orders found for the selected range.");
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text("Filtered Orders Report", 14, 20);
        doc.setFontSize(10);
        doc.text(`Filter: ${filterType.toUpperCase()} - ${filterValue}`, 14, 28);
        doc.text(`Generated on: ${dayjs().format('YYYY-MM-DD HH:mm')}`, 14, 34);

        const rows = filteredOrders.map(order => [
            order.orderId || 'N/A',
            dayjs(order.timestamp).format('YYYY-MM-DD HH:mm'),
            `Rs ${parseFloat(order.total || 0).toFixed(2)}`
        ]);

        autoTable(doc, {
            startY: 40,
            head: [['Order ID', 'Date', 'Total']],
            body: rows,
            styles: { fontSize: 10 },
        });

        // Calculate total income
        const totalIncome = filteredOrders.reduce((sum, order) => {
            return sum + parseFloat(order.total || 0);
        }, 0);

        // Add total to the bottom
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Total Income: Rs ${totalIncome.toFixed(2)}`, 14, finalY);

        doc.save(`Orders_${filterType}_${filterValue}.pdf`);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Order Report Dashboard
            </Typography>

            <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 2 }}>
                <Tab label="From localStorage" />
                <Tab label="From IndexedDB" />
            </Tabs>

            {/* Filter Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl>
                    <InputLabel>Filter Type</InputLabel>
                    <Select
                        value={filterType}
                        label="Filter Type"
                        onChange={(e) => {
                            setFilterType(e.target.value);
                            if (e.target.value === 'month') {
                                setFilterValue(dayjs().format('YYYY-MM'));
                            } else if (e.target.value === 'year') {
                                setFilterValue(dayjs().format('YYYY'));
                            } else if (e.target.value === 'week') {
                                setFilterValue(`${dayjs().year()}-W${dayjs().isoWeek().toString().padStart(2, '0')}`);
                            }
                        }}
                    >
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="week">Week</MenuItem>
                        <MenuItem value="year">Year</MenuItem>
                    </Select>
                </FormControl>

                <FormControl>
                    <InputLabel>Filter Value</InputLabel>
                    <Select
                        value={filterValue}
                        label="Filter Value"
                        onChange={(e) => setFilterValue(e.target.value)}
                    >
                        {filterType === 'year' &&
                            Array.from({ length: 5 }, (_, i) => (
                                <MenuItem key={i} value={dayjs().year() - i}>
                                    {dayjs().year() - i}
                                </MenuItem>
                            ))}
                        {filterType === 'month' &&
                            Array.from({ length: 12 }, (_, i) => {
                                const month = dayjs().month(i).format('MM');
                                const year = dayjs().year();
                                return (
                                    <MenuItem key={i} value={`${year}-${month}`}>
                                        {dayjs(`${year}-${month}-01`).format('MMMM YYYY')}
                                    </MenuItem>
                                );
                            })}
                        {filterType === 'week' &&
                            Array.from({ length: 12 }, (_, i) => {
                                const week = dayjs().subtract(i, 'week');
                                const value = `${week.year()}-W${week.isoWeek().toString().padStart(2, '0')}`;
                                return (
                                    <MenuItem key={i} value={value}>
                                        Week {week.isoWeek()} - {week.startOf('isoWeek').format('MMM D')} to {week.endOf('isoWeek').format('MMM D')}
                                    </MenuItem>
                                );
                            })}
                    </Select>
                </FormControl>

                <Button variant="contained" onClick={handleDownloadPDF}>
                    Download PDF
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    {filteredOrders.length === 0 ? (
                        <Typography>No orders found for the selected range.</Typography>
                    ) : (
                        filteredOrders.map((order, i) => (
                            <Card key={i} sx={{ mb: 2, p: 2 }}>
                                <Typography><strong>Order ID:</strong> {order.orderId}</Typography>
                                <Typography><strong>Date:</strong> {dayjs(order.timestamp).format('YYYY-MM-DD HH:mm')}</Typography>
                                <Typography><strong>Total:</strong> Rs {parseFloat(order.total || 0).toFixed(2)}</Typography>
                                <Divider sx={{ my: 1 }} />
                                {(order.items || []).map((item, idx) => (
                                    <Typography key={idx} variant="body2">
                                        {item.name} x {item.quantity} = Rs {(parseFloat(item.price) * item.quantity).toFixed(2)}
                                    </Typography>
                                ))}
                            </Card>
                        ))
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ReportDashboard;
